import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  TbBrandGithub, TbBrandLinkedin, TbBrandWhatsapp, TbCake, TbDownload, TbMail, TbMapPin,
} from 'react-icons/tb';
import '../i18n';
import {
  CV_PATH, clientLogos, contact, getAge, getYearsOfExperience, skillCategories,
} from '../data/profile';
import './assets/css/personal.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const socials = [
  { href: `mailto:${contact.email}`, icon: TbMail, label: 'Email' },
  { href: contact.whatsapp, icon: TbBrandWhatsapp, label: 'WhatsApp' },
  { href: contact.linkedin, icon: TbBrandLinkedin, label: 'LinkedIn' },
  { href: contact.github, icon: TbBrandGithub, label: 'GitHub' },
];

function Personal() {
  const { t } = useTranslation();
  const years = getYearsOfExperience();
  const techCount = skillCategories.reduce((n, c) => n + c.skills.length, 0);

  const stats = [
    { value: `${years}+`, label: t('stat_years') },
    { value: `${techCount}+`, label: t('stat_tech') },
    { value: `${clientLogos.length}+`, label: t('stat_brands') },
  ];

  return (
    <section className="hero" id="home">
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-grid-bg" aria-hidden="true" />

      <div className="hero-inner">
        <motion.div className="hero-text" initial="hidden" animate="visible">
          <motion.p className="hero-greeting" variants={fadeUp} custom={0}>
            <span className="wave" aria-hidden="true">👋</span> {t('hero_greeting')}
          </motion.p>
          <motion.h1 className="hero-name" variants={fadeUp} custom={1}>
            Tomás <span className="text-gradient">Bastiani</span>
          </motion.h1>
          <motion.h2 className="hero-role" variants={fadeUp} custom={2}>
            {t('hero_role')}
          </motion.h2>
          <motion.p className="hero-summary" variants={fadeUp} custom={3}>
            {t('hero_summary')}
          </motion.p>

          <motion.ul className="hero-meta" variants={fadeUp} custom={4}>
            <li><TbMapPin /> {t('hero_location')}</li>
            <li><TbCake /> {t('age', { age: getAge() })}</li>
          </motion.ul>

          <motion.div className="hero-cta" variants={fadeUp} custom={5}>
            <a href={CV_PATH} download="CV_Tomas_Bastiani.pdf" className="btn-primary-grad">
              <TbDownload /> {t('downloadCV')}
            </a>
            <a href="#contact" className="btn-ghost">
              {t('contact_me')}
            </a>
            <ul className="hero-socials">
              {socials.map(({ href, icon: Icon, label }) => (
                <li key={label}>
                  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}>
                    <Icon />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-photo"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="photo-frame">
            <img src="/assets/images/img-personal1.jpg" alt="Tomás Bastiani" />
          </div>
          <div className="photo-badge">
            <span className="dot" /> Thalamus · Sr Fullstack
          </div>
        </motion.div>
      </div>

      <motion.ul
        className="hero-stats"
        initial="hidden"
        animate="visible"
      >
        {stats.map((s, i) => (
          <motion.li key={s.label} variants={fadeUp} custom={6 + i}>
            <span className="stat-value">{s.value}</span>
            <span className="stat-label">{s.label}</span>
          </motion.li>
        ))}
      </motion.ul>
    </section>
  );
}

export default Personal;
